import { el, VNode } from "@dhx/ts-common/dom";
import { IBaseCoords, IBoxSize, ILineConfig, ILineText, ILineTextConfig } from "@dhx/ts-diagram";
import { getStrSize } from "@dhx/ts-common/html";
import { uid } from "@dhx/ts-common/core";
import { BaseShape } from "../Shapes/BaseShape";
import { getShapeCss } from "../templates/templates";
import { getMeta } from "../../helpers/baseMetaInfo";
import locale from "../../locales/en";
import { getDistanceFactor, getLineAngle, getLineLength } from "../../helpers/lineHelpers";

export class LineText extends BaseShape implements ILineText {
	public config: ILineTextConfig;
	public parent: ILineConfig;

	constructor(config: ILineTextConfig, parameters?) {
		super(config, parameters);
		this.id = this.config.id = config.id || uid();
		this.parent = parameters.parent;

		this.config = this.setDefaults(config);
	}
	public getMetaInfo(): { [key: string]: string }[] {
		const meta = getMeta([
			{ type: "fill", label: locale.fill },
			{ type: "text", label: locale.text },
		]);
		meta.push({ id: "textProps", type: "textProps", label: locale.textProps, verticalAlignments: false });
		return meta;
	}
	public render(): VNode {
		const zIndex =
			this.parent.$level +
			(this.parent.$group || this.parent.$selected ? (this.parent.$selected ? 3 : 2) : 1);

		const { width, height } = this.getSize(this.config.text);
		this.config.width = width > 20 ? width + 4 : 20;
		this.config.height = height > 16 ? height + 2 : 16;

		const { textAlign, fontSize, lineHeight, fontStyle, fontColor, fontWeight } = this.parent.title;

		const style = {
			textAlign: textAlign || "center",
			fontSize: fontSize || "14px",
			lineHeight: lineHeight || "14px",
			fontStyle: fontStyle || "normal",
			fontColor: fontColor || "rgba(0,0,0,0.70)",
			fontWeight: fontWeight || "normal",

			...this.config,
		};

		const { x, y } = this.getTitlePosition(this.parent, this.config);

		this.config.$x = x;
		this.config.$y = y;

		return el(
			"div",
			{
				_key: this.config.id,
				class: "dhx_diagram_line_title " + this.getCss(),
				dhx_id: this.config.id,
				parent_id: this.parent.id,
				style: {
					zIndex,
					position: "absolute",
					top: y,
					left: x,
					transform: `rotate(${this.config.$angle}deg)`,
					...getShapeCss(style),
				},
			},
			[this.getContent()]
		);
	}
	protected setDefaults(config: ILineTextConfig): ILineTextConfig {
		const editable = this.parent?.title?.editable;
		const hidden = this.parent?.title?.hidden;
		const draggable = this.parent?.title?.draggable;
		const autoPosition = this.parent?.title?.autoPosition;

		return {
			...config,

			width: config.width || 0 < 40 ? 40 : config.width,
			height: config.height || 0 < 18 ? 18 : config.height,
			editable: typeof config.editable === "boolean" ? config.editable : editable,
			hidden: typeof config.hidden === "boolean" ? config.hidden : hidden,
			draggable: typeof config.draggable === "boolean" ? config.draggable : draggable,
			autoPosition: typeof config.autoPosition === "boolean" ? config.autoPosition : autoPosition,
			distance: config.distance || 50,

			$angle: config.$angle || 0,
		};
	}
	protected getContent(): VNode {
		const { $editable, text } = this.config;
		return el(
			"span.dhx_diagram_line_title__text",
			{
				style: {
					backgroundColor: this.config.fill || this.parent.title.fill,
				},
			},
			$editable ? [this.getEditorNode()] : text
		);
	}
	protected getSize(text: string) {
		const { fontSize, lineHeight, fontWeight, fontStyle, fontColor } = this.parent.title;
		const textStyle = {
			fontFamily: "Roboto, Arial, Tahoma, Verdana, sans-serif",
			fontSize: `${this.config.fontSize ? this.config.fontSize : fontSize}px`,
			lineHeight: `${this.config.lineHeight ? this.config.lineHeight : lineHeight}px`,
			fontWeight: this.config.fontWeight || fontWeight,
			fontStyle: this.config.fontStyle || fontStyle,
			fontColor: this.config.fontColor || fontColor,
		};

		return getStrSize(text, textStyle);
	}
	protected getCenterCoords({ width, height }: ILineTextConfig, { x, y }: IBaseCoords): IBaseCoords {
		return {
			x: x - width / 2,
			y: y - height / 2,
		};
	}
	protected getStraightCoords(sp: IBaseCoords, ep: IBaseCoords, factor = 0.5): IBaseCoords {
		const { x: X1, y: Y1 } = sp;
		const { x: X2, y: Y2 } = ep;

		return { x: X1 * (1 - factor) + X2 * factor, y: Y1 * (1 - factor) + Y2 * factor };
	}
	protected getTitlePosition(line: ILineConfig, title: ILineTextConfig): IBaseCoords {
		let factor = getDistanceFactor(title.distance);

		if (line.connectType === "straight" || line.points.length === 2) {
			const sp = line.points[0];
			const ep = line.points[line.points.length - 1];

			this.config.$angle = title.autoPosition ? getLineAngle(sp, ep) : 0;

			return this.getCenterCoords(title, this.getStraightCoords(sp, ep, factor));
		}

		const points = line.points;
		const nodeLengths = [];
		let totalLength = 0;

		for (let index = 0; index < points.length - 1; index++) {
			const sp = points[index];
			const ep = points[index + 1];
			const length = getLineLength(sp, ep);

			totalLength += length;
			nodeLengths.push(length);
		}
		const currentLength = (totalLength / 100) * parseFloat(title.distance as any);

		let sp = { x: 0, y: 0 };
		let ep = { x: 0, y: 0 };

		let sumLength = 0;

		for (let index = 0; index < points.length - 1; index++) {
			sumLength += nodeLengths[index];

			if (sumLength >= currentLength) {
				sp = points[index];
				ep = points[index + 1];

				factor = 1 - getDistanceFactor(((sumLength - currentLength) / nodeLengths[index]) * 100);
				break;
			}
		}

		this.config.$angle = title.autoPosition ? getLineAngle(sp, ep) : 0;

		return this.getCenterCoords(title, this.getStraightCoords(sp, ep, factor));
	}
	public getBox(): IBoxSize {
		const { $x = 0, $y = 0, width, height } = this.config;
		const left = $x;
		const right = left + width;
		const top = $y;
		const bottom = top + height;

		return { left, right, top, bottom };
	}
}
